import {
  Box,
  BoxProps,
  ExpandableSection,
  ExpandableSectionProps,
  Grid,
  GridProps,
  Link,
  NonCancelableCustomEvent,
  Spinner,
} from '@amzn/awsui-components-core';
import {
  UserComment,
  UserCommentState,
  UserThread,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useCallback, useEffect, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getUserTitleFromProfile } from '../../api/UtilsApi';
import constants from '../../common/constants';
import '../../resources/styles/scss/components/comment/CommentBox.css';
import CommentHeaderInfo from './CommentHeaderInfo';
import CommentItem from './CommentItem';
import SendCommentForm from './SendCommentForm';

interface Props {
  thread: UserThread;
  variant?: 'left' | 'right';
  contentAuthorId?: string;
  contentCoAuthorIds?: string[];
  onThreadResolved?: (threadId: string) => Promise<void>;
  onThreadDeleted?: (threadId: string) => Promise<void>;
  onThreadUpdated?: (threadId: string) => Promise<void>;
  isEditable?: boolean;
}

const mplServiceClient = MplServiceClientFactory.create();

const ThreadBox = (props: Props) => {
  const {
    thread,
    contentAuthorId,
    contentCoAuthorIds,
    variant,
    isEditable,
    onThreadResolved,
    onThreadDeleted,
    onThreadUpdated,
  } = props;
  const { commentId } = thread;
  const [replies, setReplies] = useState<UserComment[]>([]);
  const [numReplies, setNumReplies] = useState(replies.length);
  const [loading, setLoading] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const fetchReplies = useCallback(async () => {
    setLoading(true);
    const { replies: threadReplies } = await mplServiceClient.listRepliesForThread(commentId);
    setReplies(threadReplies);
    setNumReplies(threadReplies.length);
    setLoading(false);
  }, [commentId]);

  useEffect(() => {
    if (thread.numReplies > 0 || thread.hasReply) {
      fetchReplies();
    }
  }, [props.thread.commentId, thread.numReplies, thread.hasReply, fetchReplies]);

  const submitReply = async (commentText: string) => {
    await mplServiceClient.createCommentReply(thread.commentId, commentText);
    const { replies: threadReplies } = await mplServiceClient.listRepliesForThread(commentId);
    setReplies(threadReplies);
    setNumReplies(threadReplies.length);
    setShowReplyForm(false);
  };

  const changeSectionExpand = async (
    event: NonCancelableCustomEvent<ExpandableSectionProps.ChangeDetail>,
  ) => {
    if (!numReplies) {
      setShowReplyForm(true);
      return;
    }

    const { expanded } = event.detail;
    if (expanded && replies.length > 0 && replies.length !== numReplies) {
      await fetchReplies();
    }
  };

  const replyCounterText = (replyCount: number) => {
    if (!replyCount) {
      return constants.REPLY;
    }
    const num = replyCount;
    const prefix = num === 1 ? constants.REPLY : constants.REPLIES;
    return `${prefix} (${num})`;
  };

  // tslint:disable-next-line: jsx-no-lambda
  const addReplyButton = showReplyForm ? null : (
    <Link variant="secondary" onClick={() => setShowReplyForm(true)}>
      Add reply
    </Link>
  );

  const replyForm = showReplyForm ? (
    <SendCommentForm
      onComment={submitReply}
      formPlaceholder="Replying"
      buttonText="Reply"
      buttonIcon="undo"
    />
  ) : null;

  const actionDisabled = thread.state === UserCommentState.RESOLVED || !isEditable;

  const headerAlignment: BoxProps.TextAlign = variant ?? 'right';
  const headerInfo = (
    <Box textAlign={headerAlignment}>
      <CommentHeaderInfo
        userComment={thread}
        // @ts-expect-error TS2345 - strict migration
        userTitle={getUserTitleFromProfile(thread.userProfile, contentAuthorId, contentCoAuthorIds)}
      />
    </Box>
  );

  const replyFormContainer = actionDisabled ? null : (
    <div>
      <hr />
      {addReplyButton}
      {replyForm}
    </div>
  );

  const replyList = replies.map((reply, idx) => (
    <Box variant="div" key={reply.commentId} padding={{ bottom: 'm' }}>
      <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
        <CommentItem
          userComment={reply}
          contentAuthorId={contentAuthorId}
          contentCoAuthorIds={contentCoAuthorIds}
          onCommentUpdated={fetchReplies}
          onCommentDeleted={fetchReplies}
          disabled={actionDisabled}
        />
        <CommentHeaderInfo
          userComment={reply}
          userTitle={getUserTitleFromProfile(
            reply.userProfile,
            // @ts-expect-error TS2345 - strict migration
            contentAuthorId,
            contentCoAuthorIds,
          )}
        />
      </Grid>
    </Box>
  ));

  const replySection =
    numReplies || isEditable ? (
      <ExpandableSection
        headerText={replyCounterText(numReplies)}
        onChange={changeSectionExpand}
        variant="footer"
      >
        {loading ? <Spinner size="normal" /> : replyList}
        {replyFormContainer}
      </ExpandableSection>
    ) : null;

  const threadInfo = (
    <div>
      <CommentItem
        userComment={thread}
        contentAuthorId={contentAuthorId}
        contentCoAuthorIds={contentCoAuthorIds}
        variant={variant}
        onCommentDeleted={onThreadDeleted}
        onCommentUpdated={onThreadUpdated}
        resolveEnabled
        disabled={actionDisabled}
      />
      {replySection}
    </div>
  );

  const leftBox = variant === 'left' ? threadInfo : headerInfo;
  const rightBox = variant === 'left' ? headerInfo : threadInfo;
  const gridDefinition: GridProps.ElementDefinition[] =
    variant === 'left' ? [{ colspan: 9 }, { colspan: 3 }] : [{ colspan: 3 }, { colspan: 9 }];

  return (
    <Grid gridDefinition={gridDefinition}>
      {leftBox}
      {rightBox}
    </Grid>
  );
};

export default ThreadBox;
